import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { login } from '../../store/slices/authSlice';
import { mergeGuestCartToAuth } from '../../store/slices/cartSlice';
import { RootState } from '../../types';
import { AppDispatch } from '../../store/store';
import { LoginCredentials } from '../../types';

// Validation schema
const validationSchema = Yup.object({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
});

const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error, isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const { totalItems } = useSelector((state: RootState) => state.cart);

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [cartMerged, setCartMerged] = useState<boolean>(false);

  const initialValues: LoginCredentials = {
    email: '',
    password: '',
  };

  // Reset cart merged state when component mounts or user changes
  useEffect(() => {
    setCartMerged(false);
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && !cartMerged) {
      // Merge guest cart with user cart after successful login
      const mergeCart = async () => {
        try {
          await dispatch(mergeGuestCartToAuth()).unwrap();
          setCartMerged(true);
        } catch (error) {
          console.error('Failed to merge cart:', error);
          setCartMerged(true); // Continue with redirect even if merge failed
        }
      };
      
      mergeCart();
    }
  }, [isAuthenticated, cartMerged, dispatch]);

  // Handle redirect after cart has been merged
  useEffect(() => {
    if (isAuthenticated && cartMerged) {
      if (user?.role === 'ADMIN') {
        navigate('/admin');
      } else {
        // Check if user has items in cart after merge
        if (totalItems > 0) {
          navigate('/cart');
        } else {
          navigate('/');
        }
      }
    }
  }, [isAuthenticated, cartMerged, user, totalItems, navigate]);

  const handleSubmit = async (values: LoginCredentials, { setSubmitting }: FormikHelpers<LoginCredentials>) => {
    await dispatch(login(values));
    setSubmitting(false);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Sign In
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, values, handleChange, handleBlur, isSubmitting }) => (
              <Form noValidate>
                <Field
                  as={TextField}
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
                <Field
                  as={TextField}
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading || isSubmitting}
                >
                  {loading || isSubmitting ? <CircularProgress size={24} /> : 'Sign In'}
                </Button>
                <Box sx={{ textAlign: 'center' }}>
                  <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
                    <Typography variant="body2" color="primary">
                      Forgot password?
                    </Typography>
                  </Link>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Don't have an account?{' '}
                      <Link to="/register" style={{ textDecoration: 'none' }}>
                        <Typography variant="body2" component="span" color="primary">
                          Sign Up
                        </Typography>
                      </Link>
                    </Typography>
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;